import React, { ReactNode } from "react";
import { Result } from "antd";

// ── ErrorBoundary ────────────────────────────────────────────────
// Route-level safety net. React error boundaries only catch errors thrown
// during render (not event handlers / async callbacks) — this stops an
// uncaught render TypeError from unwinding to a blank SPA. Mirrors the
// per-block boundary pattern in XoeBlockRenderer.tsx, but reusable and
// generic (accepts a custom fallback).
//
// Usage:
//   <ErrorBoundary>
//     <Routes>...</Routes>
//   </ErrorBoundary>

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught a render error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <Result
            status="error"
            title="Something went wrong"
            subTitle="Please refresh or contact support."
          />
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
